import axios from 'axios';
import { NEW_BILL_GENERATE_URL } from '../constant/apiConstants.js';

/**
 * createBillApi
 * ----------------------------------------------------------------------------
 * Sends the assembled bill to the backend and returns a normalized result.
 *
 * The backend now accepts a flat BillCreationModel:
 *   {
 *     user, token,
 *     customerName, customerMobileNo, paymentMode, schoolName,
 *     globalDiscount,                        // bill-level % discount
 *     itemPurchaseList: [ItemPurchaseModel], // NEW items
 *     itemReturnList:   [ItemReturnModel],   // RETURN / EXCHANGE items
 *     transactionModel: { cash, upi, card }
 *   }
 *
 * printType and billSendType are QUERY params, not part of the body.
 *
 * The response format depends on the store's print type:
 *   - THERMAL : JSON print payload        -> responseType 'json'
 *   - PDF/other (default) : raw PDF bytes -> responseType 'arraybuffer'
 *
 * Returns a consistent shape so the caller only branches on `isThermal`:
 *   { isThermal: boolean, data: object|ArrayBuffer, raw: AxiosResponse }
 *
 * @param {Object} params
 * @param {Object} params.billCreationModel  Full request body (see above).
 * @param {string} [params.printType='PDF']  'THERMAL' | 'PDF' | ... (case-insensitive).
 * @param {string} [params.billSendType='PRINT'] 'PRINT' | 'WHATSAPP' (case-insensitive).
 * @returns {Promise<{ isThermal: boolean, data: (object|ArrayBuffer), raw: import('axios').AxiosResponse }>}
 * @throws  Propagates any axios/network error to the caller.
 */
export const createBillApi = async ({
  billCreationModel,
  printType = 'PDF',
  billSendType = 'PRINT',
}) => {
  // Normalize so 'thermal', ' Thermal ', 'THERMAL' all match.
  const normalizedPrintType = (printType || 'PDF').trim().toUpperCase();
  const normalizedSendType = (billSendType || 'PRINT').trim().toUpperCase();
  const isThermal = normalizedPrintType === 'THERMAL';

  // Thermal -> JSON, everything else -> raw PDF bytes.
  const responseType = isThermal ? 'json' : 'arraybuffer';

  // printType and billSendType go as query params; the model is the body.
  const url =
    `${NEW_BILL_GENERATE_URL}?printType=${normalizedPrintType}` +
    `&billSendType=${normalizedSendType}`;

  const response = await axios.post(url, billCreationModel, { responseType });

  return {
    isThermal,
    data: response.data,
    raw: response,
  };
};

export default createBillApi;